import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';

interface AuditResponse {
  id: string;
  timestamp: string;
  message: string;
}

interface DownloadAuditState {
  showing: boolean;
  timestamp: string | null;
  action: string | null;
}

/**
 * Hook that wraps any download action with audit logging and a confirmation popup banner.
 *
 * Usage:
 *   const { auditedDownload, banner } = useDownloadAudit();
 *   // Then wrap your download call:
 *   await auditedDownload('Export Attendee Roster', 'attendee-roster.csv', async () => {
 *     // ... actual download logic
 *   });
 *   // Render {banner} somewhere in your component to show the popup.
 */
export function useDownloadAudit() {
  const [bannerState, setBannerState] = useState<DownloadAuditState>({
    showing: false,
    timestamp: null,
    action: null,
  });

  const auditedDownload = useCallback(async (
    action: string,
    fileName: string | undefined,
    downloadFn: () => Promise<void> | void,
  ) => {
    // Log the download BEFORE executing it
    let auditTimestamp: string | null = null;
    try {
      const result = await apiClient<AuditResponse>('/audit/data-download', {
        method: 'POST',
        body: { action, fileName },
      });
      auditTimestamp = result.timestamp;
    } catch {
      // Don't block the download if audit fails — but still show banner
      auditTimestamp = new Date().toISOString();
    }

    // Show the banner
    setBannerState({ showing: true, timestamp: auditTimestamp, action });

    // Execute the actual download
    await downloadFn();

    // Auto-hide banner after 6 seconds
    setTimeout(() => {
      setBannerState((prev) => (prev.action === action ? { ...prev, showing: false } : prev));
    }, 6000);
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerState((prev) => ({ ...prev, showing: false }));
  }, []);

  return { auditedDownload, bannerState, dismissBanner };
}
